import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  IconButton,
  TextField,
  CircularProgress,
  Paper,
  Chip,
  Box,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import RefreshIcon from "@material-ui/icons/Refresh";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import axios from "axios";
import { toast } from "react-toastify";
import { useParams, useNavigate } from "react-router-dom";
import { decryptData } from "../../../crypto";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "16px 24px 12px 24px",
    borderBottom: "1px solid #e0e0e0",
  },
  titleText: {
    fontWeight: 700,
    color: "#00577B",
    fontSize: "1.25rem",
  },
  topControls: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    marginBottom: "16px",
    marginTop: "8px",
    flexWrap: "wrap",
  },
  inputField: {
    flex: 1,
    minWidth: "220px",
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      "&.Mui-focused fieldset": {
        borderColor: "#00577B",
      },
    },
    "& .MuiInputLabel-outlined.Mui-focused": {
      color: "#00577B",
    },
  },
  custombtnblue: {
    background: "#00577B !important",
    color: "white !important",
    fontWeight: "600 !important",
    textTransform: "capitalize !important",
    padding: "8px 20px !important",
    borderRadius: "8px !important",
    height: "40px",
    "&:hover": {
      background: "#004360 !important",
    },
  },
  custombtngreen: {
    background: "#007B55 !important",
    color: "white !important",
    fontWeight: "600 !important",
    textTransform: "capitalize !important",
    padding: "8px 24px !important",
    borderRadius: "8px !important",
    height: "40px",
    "&:hover": {
      background: "#005e41 !important",
    },
  },
  custombtnoutline: {
    color: "#555 !important",
    borderColor: "#ccc !important",
    fontWeight: "600 !important",
    textTransform: "capitalize !important",
    padding: "8px 20px !important",
    borderRadius: "8px !important",
  },
  questionPaperContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    marginTop: "12px",
  },
  questionCard: {
    padding: "16px 20px",
    borderRadius: "10px",
    border: "1px solid #e2e8f0",
    backgroundColor: "#ffffff",
    boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.03)",
  },
  questionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "10px",
    paddingBottom: "6px",
    borderBottom: "1px dashed #e0e0e0",
  },
  questionText: {
    fontSize: "14.5px",
    color: "#1e293b",
    lineHeight: "1.6",
    marginBottom: "12px",
    "& img": {
      maxWidth: "100%",
      height: "auto",
      display: "block",
      marginTop: "8px",
      marginBottom: "8px",
      borderRadius: "4px",
    },
    "& p": {
      margin: "4px 0",
    },
  },
  optionsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    marginTop: "10px",
  },
  optionBox: {
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
    padding: "10px 14px",
    borderRadius: "8px",
    transition: "all 0.2s ease",
  },
  optionText: {
    flex: 1,
    fontSize: "14px",
    lineHeight: "1.5",
    "& img": {
      maxWidth: "100%",
      height: "auto",
      display: "block",
      marginTop: "4px",
      marginBottom: "4px",
    },
    "& p": {
      margin: "2px 0",
    },
  },
}));

const UnadacemyQpSyncModal = ({ open, handleClose, paperCode, rowId, onSuccess }) => {
  const classes = useStyles();
  const params = useParams();
  const navigate = useNavigate();
  const effectiveRowId = rowId || params?.rowId;

  const [quizUid, setQuizUid] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [qpData, setQpData] = useState(null);

  useEffect(() => {
    if (paperCode) {
      setQuizUid(paperCode);
    }
  }, [paperCode]);

  useEffect(() => {
    if (open && (paperCode || quizUid)) {
      fetchVendorQuestionPaper(paperCode || quizUid);
    } else if (!open) {
      setQpData(null);
    }
  }, [open]);

  const fetchVendorQuestionPaper = async (overrideUid) => {
    const targetUid = (overrideUid !== undefined && typeof overrideUid === "string") ? overrideUid : quizUid;
    if (!targetUid || !targetUid.trim()) {
      toast.warn("Quiz UID (Paper Code) is required.");
      return;
    }

    setLoading(true);
    try {
      const token = decryptData(sessionStorage.getItem("token"));
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/v1/b2b/question/unacademy/get-questions-api`,
        {
          quiz_uid: targetUid.trim(),
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      console.log("Vendor CBT Question Paper Response:", response.data);

      const resData = response.data?.data || response.data;
      if (resData && (resData.questions || Array.isArray(resData))) {
        const formattedData = Array.isArray(resData)
          ? { exam_code: targetUid, questions: resData }
          : resData;

        setQpData(formattedData);
        toast.success(response.data?.message || "Unacademy question paper fetched successfully!");
      } else {
        toast.error(response.data?.message || "Failed to fetch Unacademy question paper.");
      }
    } catch (error) {
      console.error("Error fetching vendor CBT question paper:", error);
      const errMsg =
        error?.response?.data?.message ||
        "Error fetching vendor CBT question paper.";
      toast.error(errMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadQuestions = async () => {
    if (!qpData || !questionsList || questionsList.length === 0) {
      toast.warn("No questions loaded to upload.");
      return;
    }
    if (!effectiveRowId) {
      toast.error("Exam ID is missing.");
      return;
    }

    setUploading(true);
    try {
      const token = decryptData(sessionStorage.getItem("token"));
      const endpoint = `${process.env.REACT_APP_API_BASE_URL}/v1/b2b/question/add/import/uid/json/${effectiveRowId}`;

      const response = await axios.post(endpoint, qpData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      console.log("Upload Questions Response:", response.data);

      if (response.status === 200 || response.data?.success) {
        toast.success(response.data?.message || "Questions uploaded successfully!");
        if (typeof onSuccess === "function") {
          onSuccess(response.data);
        } else {
          if (typeof handleClose === "function") {
            handleClose();
          }
          if (effectiveRowId && navigate) {
            navigate(`/admin/addquestion/${effectiveRowId}`);
          }
        }
      } else {
        toast.error(response.data?.message || "Failed to upload questions.");
      }
    } catch (error) {
      console.error("Error uploading questions:", error);
      const errMsg =
        error?.response?.data?.message ||
        "Error uploading questions to exam.";
      toast.error(errMsg);
    } finally {
      setUploading(false);
    }
  };

  const questionsList = qpData?.questions || (Array.isArray(qpData) ? qpData : []);
  const examCode = qpData?.exam_code || quizUid;

  return (
    <Dialog
      open={open !== undefined ? open : true}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        style: { borderRadius: "12px", maxHeight: "90vh" },
      }}
    >
      <div className={classes.dialogTitle}>
        <div>
          <Typography variant="h6" className={classes.titleText}>
            Unacademy Question Paper Sync & Upload
          </Typography>
          {examCode && (
            <Typography variant="caption" color="textSecondary">
              Exam Code: <strong style={{ color: "#00577B" }}>{examCode}</strong>
            </Typography>
          )}
        </div>
        <IconButton onClick={handleClose} size="small" disabled={loading || uploading}>
          <CloseIcon />
        </IconButton>
      </div>

      <DialogContent dividers style={{ padding: "20px 24px", backgroundColor: "#f8fafc" }}>
        <div className={classes.topControls}>
          <TextField
            label="Quiz UID / Paper Code"
            variant="outlined"
            size="small"
            value={quizUid}
            onChange={(e) => setQuizUid(e.target.value)}
            className={classes.inputField}
            placeholder="e.g. JEY3Q3ETRX"
            disabled={true}
          />
          {/* <Button
            className={classes.custombtnblue}
            onClick={() => fetchVendorQuestionPaper()}
            disabled={loading || uploading}
            variant="contained"
            startIcon={loading ? <CircularProgress size={18} style={{ color: "white" }} /> : <RefreshIcon />}
          >
            {loading ? "Fetching..." : "Fetch Questions"}
          </Button> */}

          {/* {questionsList && questionsList.length > 0 && (
            <Button
              className={classes.custombtngreen}
              onClick={handleUploadQuestions}
              disabled={loading || uploading}
              variant="contained"
              startIcon={uploading ? <CircularProgress size={18} style={{ color: "white" }} /> : <CloudUploadIcon />}
            >
              {uploading ? "Uploading..." : "Upload Questions"}
            </Button>
          )} */}
        </div>

        {(loading || uploading) && (
          <Box style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "220px", gap: "12px" }}>
            <CircularProgress style={{ color: uploading ? "#007B55" : "#00577B" }} />
            <Typography variant="body2" color="textSecondary">
              {uploading ? "Uploading questions to exam..." : "Fetching Unacademy Question Paper..."}
            </Typography>
          </Box>
        )}

        {!loading && !uploading && questionsList && questionsList.length > 0 && (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
              <Chip
                label={`Total Questions: ${questionsList.length}`}
                color="primary"
                size="small"
                style={{ backgroundColor: "#00577B", fontWeight: 600 }}
              />
            </div>

            <div className={classes.questionPaperContainer}>
              {questionsList.map((q, qIdx) => (
                <Paper key={q.question_uid || qIdx} className={classes.questionCard} elevation={0}>
                  <div className={classes.questionHeader}>
                    <Typography variant="subtitle2" style={{ fontWeight: 700, color: "#00577B" }}>
                      Q{q.q_prifix || qIdx + 1}.
                    </Typography>
                    {q.question_uid && (
                      <Chip
                        label={`UID: ${q.question_uid}`}
                        size="small"
                        variant="outlined"
                        style={{ height: "22px", fontSize: "11px", color: "#475569", borderColor: "#cbd5e1" }}
                      />
                    )}
                  </div>

                  <div
                    className={classes.questionText}
                    dangerouslySetInnerHTML={{ __html: q.question_text || "" }}
                  />

                  {q.options && q.options.length > 0 && (
                    <div className={classes.optionsContainer}>
                      {q.options.map((opt, optIdx) => {
                        const isCorrect = opt.correct_answer === true;
                        return (
                          <div
                            key={opt.response_uid || optIdx}
                            className={classes.optionBox}
                            style={{
                              backgroundColor: isCorrect ? "#f0fdf4" : "#ffffff",
                              border: isCorrect ? "1.5px solid #22c55e" : "1px solid #e2e8f0",
                            }}
                          >
                            <span
                              style={{
                                fontWeight: 700,
                                fontSize: "13px",
                                minWidth: "24px",
                                color: isCorrect ? "#15803d" : "#64748b",
                              }}
                            >
                              ({opt.q_prifix || String.fromCharCode(65 + optIdx)})
                            </span>
                            <div
                              className={classes.optionText}
                              style={{ color: isCorrect ? "#14532d" : "#334155" }}
                              dangerouslySetInnerHTML={{ __html: opt.response_text || "" }}
                            />
                            {isCorrect && (
                              <Chip
                                icon={<CheckCircleIcon style={{ fontSize: "14px", color: "#166534" }} />}
                                label="Correct"
                                size="small"
                                style={{
                                  backgroundColor: "#dcfce7",
                                  color: "#166534",
                                  fontWeight: 600,
                                  fontSize: "11px",
                                  height: "24px",
                                }}
                              />
                            )}
                            {opt.response_uid && (
                              <Typography variant="caption" style={{ color: "#94a3b8", fontSize: "11px" }}>
                                {opt.response_uid}
                              </Typography>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </Paper>
              ))}
            </div>
          </div>
        )}

        {!loading && !uploading && (!questionsList || questionsList.length === 0) && (
          <Box style={{ textAlign: "center", padding: "40px 20px", color: "#64748b" }}>
            <Typography variant="body1" style={{ fontWeight: 500 }}>
              No question paper data loaded.
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginTop: "4px" }}>
              Enter Quiz UID and click "Fetch Questions".
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions style={{ padding: "12px 24px", borderTop: "1px solid #e0e0e0" }}>
        <Button
          className={classes.custombtnoutline}
          onClick={handleClose}
          disabled={loading || uploading}
          variant="outlined"
        >
          Close
        </Button>
        {questionsList && questionsList.length > 0 && (
          <Button
            className={classes.custombtngreen}
            onClick={handleUploadQuestions}
            disabled={loading || uploading}
            variant="contained"
            startIcon={uploading ? <CircularProgress size={18} style={{ color: "white" }} /> : <CloudUploadIcon />}
          >
            {uploading ? "Uploading..." : "Upload Questions"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default UnadacemyQpSyncModal;
